/**
 * API Service for StockPro
 * This service handles all API requests and offline fallbacks
 */
import axios from 'axios';
import apiConfig from '../config/api';

// Create an axios instance with our configuration
const api = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  withCredentials: apiConfig.withCredentials
});

// Flag to track online/offline status
let isOnline = navigator.onLine;

// Track online/offline status changes
window.addEventListener('online', () => { isOnline = true; });
window.addEventListener('offline', () => { isOnline = false; });

// Function to save data to localStorage for offline use
const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error(`Error saving to localStorage (${key}):`, error);
    return false;
  }
};

// Function to load data from localStorage
const loadFromLocalStorage = (key, defaultValue = []) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch (error) {
    console.error(`Error loading from localStorage (${key}):`, error);
    return defaultValue;
  }
};

// Response interceptor to handle errors and offline mode
api.interceptors.response.use(
  response => {
    // Cache successful responses for offline use
    const url = response.config.url;
    
    // Only cache GET requests
    if (response.config.method === 'get' && response.data) {
      // Determine the appropriate cache key based on the URL
      let cacheKey;
      if (url.includes('/products')) {
        cacheKey = 'stockpro_products';
      } else if (url.includes('/customers')) {
        cacheKey = 'stockpro_customers';
      } else if (url.includes('/suppliers')) {
        cacheKey = 'stockpro_suppliers';
      } else if (url.includes('/stock')) {
        cacheKey = 'stockpro_stock';
      } else if (url.includes('/categories')) {
        cacheKey = 'stockpro_product_categories';
      } else if (url.includes('/warehouses')) {
        cacheKey = 'stockpro_warehouses';
      }
      
      // Cache the response
      if (cacheKey) {
        saveToLocalStorage(cacheKey, response.data);
        console.log(`Cached API response to ${cacheKey}`);
        
        // Also update legacy localStorage keys for compatibility
        if (cacheKey === 'stockpro_products') {
          saveToLocalStorage('products', response.data);
        }
      }
    }
    
    return response;
  },
  error => {
    // Handle offline mode
    if (!isOnline || (error.message && error.message.includes('Network Error'))) {
      console.log('App is offline or network error. Using cached data if available.');
      
      const url = error.config.url;
      let cacheKey;
      
      // Determine which cache to use based on the URL
      if (url.includes('/products')) {
        cacheKey = 'stockpro_products';
      } else if (url.includes('/customers')) {
        cacheKey = 'stockpro_customers';
      } else if (url.includes('/suppliers')) {
        cacheKey = 'stockpro_suppliers';
      } else if (url.includes('/stock')) {
        cacheKey = 'stockpro_stock';
      } else if (url.includes('/categories')) {
        cacheKey = 'stockpro_product_categories';
      } else if (url.includes('/warehouses')) {
        cacheKey = 'stockpro_warehouses';
      }
      
      if (cacheKey && error.config.method === 'get') {
        // For GET requests, return cached data
        const cachedData = loadFromLocalStorage(cacheKey);
        if (cachedData) {
          console.log(`Using cached data from ${cacheKey}`);
          return Promise.resolve({
            data: cachedData,
            status: 200,
            statusText: 'OK (Offline)',
            headers: {},
            config: error.config,
            offline: true
          });
        }
      } else if (isOnline === false) {
        // For non-GET requests in offline mode, store them for later sync
        const pendingActions = loadFromLocalStorage('stockpro_pending_actions', []);
        
        pendingActions.push({
          method: error.config.method,
          url: error.config.url,
          data: error.config.data ? JSON.parse(error.config.data) : null,
          timestamp: new Date().toISOString()
        });
        
        saveToLocalStorage('stockpro_pending_actions', pendingActions);
        console.log('Action saved for later sync:', error.config.method, error.config.url);
        
        // For mutations, update local data immediately to provide instant feedback
        if (error.config.method === 'post' || error.config.method === 'put' || error.config.method === 'delete') {
          const fakeResponse = handleOfflineMutation(error.config);
          if (fakeResponse) {
            return Promise.resolve(fakeResponse);
          }
        }
      }
    }
    
    // Log API errors
    console.error('API Error:', error.response || error.message);
    
    // Rethrow the error for component-level handling
    return Promise.reject(error);
  }
);

// Request interceptor to add auth token
api.interceptors.request.use(
  config => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Function to handle mutations (POST, PUT, DELETE) in offline mode
const handleOfflineMutation = (config) => {
  const { method, url, data } = config;
  let cacheKey, entityId;
  
  // Extract entity ID from URL if present
  const urlParts = url.split('/');
  if (urlParts.length > 0) {
    entityId = urlParts[urlParts.length - 1];
    // Skip if the last part is not an ID (e.g., "/products")
    if (entityId === 'products' || entityId === 'customers' || entityId === 'suppliers') {
      entityId = null;
    }
  }
  
  // Determine which cache to use
  if (url.includes('/products')) {
    cacheKey = 'stockpro_products';
  } else if (url.includes('/customers')) {
    cacheKey = 'stockpro_customers';
  } else if (url.includes('/suppliers')) {
    cacheKey = 'stockpro_suppliers';
  } else if (url.includes('/stock')) {
    cacheKey = 'stockpro_stock';
  } else {
    return null; // Unknown entity type
  }
  
  const cachedData = loadFromLocalStorage(cacheKey, []);
  let updatedData;
  let responseData = null;
  
  if (method === 'post') {
    // Creating a new entity
    const newEntity = {
      ...data,
      id: data.id || `${cacheKey.split('_')[1].slice(0, 3).toUpperCase()}-${Date.now().toString(36)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _offlineCreated: true
    };
    
    updatedData = [...cachedData, newEntity];
    responseData = newEntity;
  } 
  else if (method === 'put' && entityId) {
    // Updating an entity
    const index = cachedData.findIndex(item => item.id === entityId);
    if (index !== -1) {
      updatedData = [...cachedData];
      updatedData[index] = {
        ...updatedData[index],
        ...data,
        updatedAt: new Date().toISOString(),
        _offlineUpdated: true
      };
      responseData = updatedData[index];
    } else {
      return null; // Entity not found
    }
  } 
  else if (method === 'delete' && entityId) {
    // Deleting an entity
    updatedData = cachedData.filter(item => item.id !== entityId);
    responseData = { message: 'Entity deleted (offline)' };
  }
  
  if (updatedData) {
    // Update the cache
    saveToLocalStorage(cacheKey, updatedData);
    
    // Also update legacy localStorage keys for compatibility
    if (cacheKey === 'stockpro_products') {
      saveToLocalStorage('products', updatedData);
    }
    
    // Dispatch events to notify components
    window.dispatchEvent(new CustomEvent('offline-data-updated', {
      detail: { cacheKey, method, entityId, data: responseData }
    }));
    
    // Return a fake response
    return {
      data: responseData,
      status: method === 'post' ? 201 : 200,
      statusText: 'OK (Offline)',
      headers: {},
      config,
      offline: true
    };
  }
  
  return null;
};

// Function to sync pending actions when coming back online
const syncPendingActions = async () => {
  if (!isOnline) return;
  
  const pendingActions = loadFromLocalStorage('stockpro_pending_actions', []);
  if (pendingActions.length === 0) return;
  
  console.log(`Syncing ${pendingActions.length} pending actions...`);
  
  const completedActions = [];
  
  for (const action of pendingActions) {
    try {
      const { method, url, data } = action;
      
      // Make the actual API call
      let response;
      if (method === 'post') {
        response = await api.post(url, data);
      } else if (method === 'put') {
        response = await api.put(url, data);
      } else if (method === 'delete') {
        response = await api.delete(url);
      }
      
      console.log(`Synced action: ${method} ${url}`);
      completedActions.push(action);
    } catch (error) {
      console.error('Error syncing action:', action, error);
    }
  }
  
  // Remove completed actions
  if (completedActions.length > 0) {
    const remainingActions = pendingActions.filter(
      action => !completedActions.includes(action)
    );
    
    saveToLocalStorage('stockpro_pending_actions', remainingActions);
    console.log(`Synced ${completedActions.length} actions, ${remainingActions.length} remaining.`);
    
    // Refresh data after sync
    await refreshCachedData();
  }
};

// Function to refresh cached data from the API
const refreshCachedData = async () => {
  if (!isOnline) return;
  
  try {
    // Refresh products
    const productsResponse = await api.get('/products');
    
    // Refresh customers
    const customersResponse = await api.get('/customers');
    
    // Refresh suppliers (if endpoint exists)
    try {
      await api.get('/suppliers');
    } catch (error) {
      console.log('Suppliers endpoint not available or error:', error);
    }
    
    console.log('Cache refreshed with latest data from API');
    return {
      products: productsResponse.data,
      customers: customersResponse.data
    };
  } catch (error) {
    console.error('Error refreshing cached data:', error);
    return null;
  }
};

// Set up event listener to sync when coming back online
window.addEventListener('online', () => {
  console.log('Back online, syncing pending actions...');
  syncPendingActions();
});

// The main API object with enhanced methods
const enhancedApi = {
  ...api,
  
  // Core methods that handle both online and offline modes
  async getProducts() {
    return api.get('/products');
  },
  
  async getProduct(id) {
    return api.get(`/products/${id}`);
  },
  
  async createProduct(product) {
    return api.post('/products', product);
  },
  
  async updateProduct(id, product) {
    return api.put(`/products/${id}`, product);
  },
  
  async deleteProduct(id) {
    return api.delete(`/products/${id}`);
  },
  
  async getCustomers() {
    return api.get('/customers');
  },
  
  async getCustomer(id) {
    return api.get(`/customers/${id}`);
  },
  
  async createCustomer(customer) {
    return api.post('/customers', customer);
  },
  
  async updateCustomer(id, customer) {
    return api.put(`/customers/${id}`, customer);
  },
  
  async deleteCustomer(id) {
    return api.delete(`/customers/${id}`);
  },
  
  async createStockMovement(movement) {
    return api.post('/stock', movement);
  },
  
  // Utility methods
  isOffline() {
    return !isOnline;
  },
  
  syncPendingActions,
  refreshCachedData
};

export default enhancedApi;